import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase, retryRequest } from '@/lib/supabase';
import { UserProfile } from '@/types/database';
import { cn } from '@/lib/utils';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Footer } from './Footer';
import { ErrorDisplay } from '@/components/ui/ErrorDisplay';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, session, loading, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { organizationName } = useOrganization();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.email?.endsWith('@admin.com');

  useEffect(() => {
    if (!session && !loading && location.pathname !== '/login' && location.pathname !== '/register') {
      navigate('/login');
    }
  }, [session, loading, navigate, location.pathname]);

  useEffect(() => {
    async function loadProfile() {
      if (!user) return;

      try {
        setError(null);

        // Use retryRequest for better reliability
        const { data: existingProfile, error: fetchError } = await retryRequest(() =>
          supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', user.id)
            .maybeSingle()
        );

        if (fetchError) {
          throw new Error(`Failed to fetch profile: ${fetchError.message}`, { cause: fetchError });
        }

        if (existingProfile) {
          setProfile(existingProfile);
        } else {
          // Create new profile if it doesn't exist
          const { data: newProfile, error: createError } = await retryRequest(() =>
            supabase
              .from('user_profiles')
              .insert([{ user_id: user.id }])
              .select()
              .single()
          );

          if (createError) {
            throw new Error(`Failed to create profile: ${createError.message}`, { cause: createError });
          }

          setProfile(newProfile);
        }
      } catch (err) {
        console.error('Error in loadProfile:', err);
        setError(err instanceof Error ? err : new Error('Failed to load profile'));
      }
    }

    loadProfile();
  }, [user]);

  const handleSignOut = async () => {
    try {
      setShowUserMenu(false);
      setShowThemeMenu(false);
      setProfile(null);
      await signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during sign out:', error);
      window.location.href = '/login';
    }
  };

  const getPageTitle = () => {
    let pageTitle = '';

    switch (location.pathname) {
      case '/':
        pageTitle = 'Dashboard';
        break;
      case '/library':
        pageTitle = 'Library';
        break;
      case '/reports':
        pageTitle = 'Reports';
        break;
      case '/support':
        pageTitle = 'Support';
        break;
      case '/settings':
        pageTitle = 'Settings';
        break;
      case '/organizations':
        pageTitle = 'Organizations';
        break;
      case '/users':
        pageTitle = 'Users';
        break;
      case '/surveys':
        pageTitle = 'Surveys';
        break;
      case '/strategy':
        pageTitle = 'AI Strategy';
        break;
      case '/tools':
        pageTitle = 'Tools';
        break;
      default:
        if (location.pathname.startsWith('/surveys/')) {
          if (location.pathname.includes('/respond/')) {
            pageTitle = 'Survey Response';
          } else if (location.pathname.includes('/respondents')) {
            pageTitle = 'Survey Respondents';
          } else if (location.pathname.includes('/reports/')) {
            pageTitle = 'Survey Report';
          }
        }
        break;
    }

    // Update document title
    document.title = `${pageTitle}${organizationName ? ` | ${organizationName}` : ''} | AI Prime Consulting`;

    return isAdmin ? pageTitle : organizationName ? `${organizationName} ${pageTitle}` : pageTitle;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Sidebar */}
      <Sidebar isAdmin={isAdmin} />
      
      {/* Mobile overlay */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Main content area */}
      <div className={cn(
        "flex-1 flex flex-col min-h-screen relative",
        isCollapsed ? "lg:ml-16" : "lg:ml-64"
      )}>
        {/* Header */}
        <Header
          title={getPageTitle()}
          profile={profile}
          userEmail={user?.email}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          showUserMenu={showUserMenu}
          setShowUserMenu={setShowUserMenu}
          onSignOut={handleSignOut}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay 
            error={error}
            context="Layout"
            className="m-4"
          />
        )}

        {/* Main content */}
        <main className="flex-1 container mx-auto px-4 sm:px-6 py-6 pb-20 overflow-x-hidden">
          <div className="max-w-full">
            {children}
          </div>
        </main>

        {/* Footer */}
        <div className="sticky bottom-0 w-full bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="container mx-auto">
            <Footer
              theme={theme}
              setTheme={setTheme}
              showThemeMenu={showThemeMenu}
              setShowThemeMenu={setShowThemeMenu}
            />
          </div>
        </div>
      </div>
    </div>
  );
}